import {HeaderAgentTasks, type HeaderAgentTasksFragment} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import {useIsVisualizationEnabled} from '@github-ui/issue-agent-tasks/utils/is-visualization-enabled'
import {Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {OptionConfig} from '../OptionConfig'
import type {StickyHeaderChips$key} from './__generated__/StickyHeaderChips.graphql'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import type {HeaderIssueTypeFragment} from './HeaderIssueType'
import {SmallHeaderIssueType} from './HeaderIssueType'
import {HeaderSubIssueSummary, type HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {SmallRepositoryPill} from './RepositoryPill'
import styles from './StickyHeaderChips.module.css'
import {SmallTrackedBy, type TrackedByFragment} from './TrackedBy'

export type StickyHeaderChipsProps = {
  issueKey: StickyHeaderChips$key
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  optionConfig: OptionConfig
}

export function StickyHeaderChips({
  issueKey,
  linkedSecurityAlertsData,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  optionConfig,
}: StickyHeaderChipsProps) {
  const showAgentTasksPanel = useIsVisualizationEnabled()
  const {showRepositoryPill} = optionConfig

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickyHeaderChips on Issue {
        url
        repository {
          nameWithOwner
          name
          owner {
            login
          }
          isPrivate
        }
        issueType {
          name
          color
        }
        subIssuesSummary {
          total
          completed
        }
        agentAssignments {
          __typename
        }
        linkedPullRequestsIncludingClosed: closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            id
            number
            url
            state
            databaseId
          }
        }
      }
    `,
    issueKey,
  )

  return (
    <div className={styles.chipsRow}>
      {/* RELAY-CLEANUP(StickyHeaderChips): open-enum `color` from the still-Relay parent.
          Remove when StickyHeaderChips leaves Relay. */}
      <SmallHeaderIssueType data={data as HeaderIssueTypeFragment} />
      {/* RELAY-CLEANUP(StickyHeaderChips): open-enum `state` from the still-Relay parent.
          Remove when StickyHeaderChips leaves Relay. */}
      {showAgentTasksPanel && <HeaderAgentTasks issueData={data as HeaderAgentTasksFragment} isSticky />}
      <HeaderSubIssueSummary subIssuePrimaryKey={data} subIssueSecondaryKey={subIssueSecondaryData} size="small" />
      {linkedSecurityAlertsData && (
        <>
          <HeaderBlockedBySummary blockedBySecondaryData={blockedBySecondaryData} size="small" />
          <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} isSmall />
          <Suspense>
            <SmallTrackedBy url={data.url} trackedByData={trackedBySecondaryData} />
          </Suspense>
        </>
      )}
      {showRepositoryPill && <SmallRepositoryPill repositoryPillData={data} />}
    </div>
  )
}
